import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MeterPointConfig } from "../../../api/meters";

export type TabId = string;

export const useTabManager = (tabs: MeterPointConfig[]) => {
  const { meterPointId, meterId } = useParams();

  // Generate valid tabs from configs
  const tabIdFromMeterPointMap: Record<string, Array<string>> = useMemo(() => {
    return tabs
      .filter((t) => t)
      .reduce((acc, meterPoint) => {
        return {
          ...acc,
          [meterPoint.id]: meterPoint.meters.map((meter) => meter.id),
        };
      }, {});
  }, [tabs]);

  const tabIds = useMemo(() => {
    return Object.values(tabIdFromMeterPointMap).reduce(
      (collection: string[], ids) => {
        return [
          ...collection,
          ...ids.map((id) => {
            return id;
          }),
        ];
      },
      [],
    );
  }, [tabIdFromMeterPointMap]);

  /**
   * How to determine the active tab:
   * 1. URL takes priority if meter point id and meter id are given and valid
   * 2. Default to first meter id within the URL meter point id
   * 3. Default to first meter
   * 4. Default to false (no active tabs)
   */
  const getActiveTab = useCallback((): TabId | false => {
    if (meterId) return meterId;

    if (
      Object.keys(tabIdFromMeterPointMap).length &&
      meterPointId &&
      typeof meterId === "undefined"
    ) {
      // find first tab id matching the meter point id
      return tabIdFromMeterPointMap?.[meterPointId]?.[0] ?? false;
    }

    return false;
  }, [meterPointId, meterId, tabIdFromMeterPointMap]);

  const [active, setActive] = useState<TabId | false>(getActiveTab);

  // To ensure that if a modal opens that doesn't specify a meter id,
  // it will set it as needed.
  useEffect(() => {
    if (!active) {
      const target = getActiveTab();
      setActive(target);
    }
  }, [active, setActive, tabIdFromMeterPointMap, getActiveTab]);

  return {
    tabIds,
    tabIdFromMeterPointMap,
    activeTabId: active,
    setActiveTabId: setActive,
  };
};
